/* eslint-disable no-prototype-builtins */
import { withSize } from 'react-sizeme';
import React, { useEffect, useState } from 'react';
import capitalize from 'lodash/capitalize';
import fromPairs from 'lodash/fromPairs';
import lowerCase from 'lodash/lowerCase';
import { Box, Dialog, DialogContent, DialogTitle, Divider, SortAmountDownIcon, SortAmountUpIcon, Typography, makeStyles, useModal, useTheme, } from '../..';
import { CardTableContext, } from './Context';
import { CardTableHeader, CardTablePaginationControls } from './index';
import { checkboxColumnDef } from './Shared';
const useStyles = makeStyles(({ spacing, palette }) => ({
    columnSortIcon: {
        width: spacing(2),
        fontSize: '1.1em',
        marginLeft: spacing(0.5),
    },
    expansionTableHeader: {
        height: spacing(6),
        marginLeft: spacing(3),
        marginTop: spacing(1),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        overflowX: 'hidden',
    },
    columnHover: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
}));
// This is an approximate value (in px) to reserve for space in each row that
// can not be occupied by cell contents. It is (loosely) calculated based on
// ~50px for left/right padding, ~5px for borders, and ~75px for expand & actions
// buttons.
const RESERVED_SPACE = 135;
let sizeUpdates = {};
var timer = {};
function debounce(cb, duration, name) {
    return function (..._) {
        const arg_data = arguments;
        clearTimeout(timer[name]);
        timer[name] = setTimeout(function () {
            cb.apply(null, arg_data);
        }, duration);
    };
}
export const applySelectionMappingDelta = (delta) => (previous) => ({
    ...previous,
    ...delta,
});
export const CardTableCore = ({ colDefs, onSort, header, filter, expandableFilters, filtersExpanded, numActiveFilters, children, size, selection, onSelectionChange, onGlobalSelectionChange, headerCheckboxState, ...paginationProps }) => {
    if (selection)
        colDefs = [checkboxColumnDef, ...colDefs];
    const { visibleColumns, hiddenColumns, isMobile } = getColumns(colDefs, size);
    const classes = useStyles({ isMobile, highlightColor: undefined });
    const [sortBy, setSortBy] = useState();
    const theme = useTheme();
    const [sortDirection, setSortDirection] = useState();
    const [colWidths, setColumnWidths] = useState({});
    const [elements, setElements] = useState({});
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [isResizing, setIsResizing] = useState(false);
    const [lastWidth, setLastWidth] = useState(size.width);
    const [showModal, hideModal] = useModal(() => (React.createElement(Dialog, { open: true, onClose: hideModal },
        React.createElement(DialogTitle, { onClose: hideModal }, "Filter"),
        React.createElement(Divider, null),
        React.createElement(DialogContent, null, filter && (typeof filter === 'function' ? filter(hideModal) : filter)))), [filter]);
    const setRowExpanded = (key, expanded) => {
        setExpandedRows(expandedRowsOld => {
            if (expanded) {
                return new Set([...Array.from(expandedRowsOld), key]);
            }
            return new Set(Array.from(expandedRowsOld).filter(s => s !== key));
        });
    };
    const handleClickSort = (colName) => {
        if (sortBy === colName) {
            if (sortDirection === 'asc') {
                setSortDirection(undefined);
                setSortBy(undefined);
                onSort && onSort(colName, undefined);
            }
            else if (sortDirection === 'desc') {
                setSortDirection('asc');
                onSort && onSort(colName, 'asc');
            }
        }
        else {
            setSortBy(colName);
            setSortDirection('desc');
            onSort && onSort(colName, 'desc');
        }
    };
    const tableName = (header && header.toString()) || '';
    const debouncedSetColmnWidths = debounce(setColumnWidths, 500, tableName + 'col_widths');
    const debouncedSetResizing = debounce(setIsResizing, 500, tableName + 'resize');
    const onSizeChange = (name, width) => {
        if (!(tableName in sizeUpdates)) {
            sizeUpdates[tableName] = {};
        }
        sizeUpdates[tableName][name] = width;
        debouncedSetColmnWidths((_) => {
            return { ...widthSum, ...sizeUpdates[tableName] };
        });
    };
    const width = size.width;
    const checkedProps = paginationProps.pageSize ? paginationProps : undefined; // 'pageSize' in paginationProps ? paginationProps : undefined
    useEffect(() => {
        if (lastWidth !== width) {
            setIsResizing(true);
            debouncedSetResizing(false);
            setLastWidth(width);
        }
    }, [width, lastWidth, setIsResizing, debouncedSetResizing]);
    const topSelectionChangeHandler = (state) => {
        onGlobalSelectionChange && onGlobalSelectionChange(state);
    };
    let topCheckmarkState = headerCheckboxState;
    if (onGlobalSelectionChange && selection && !topCheckmarkState) {
        if (paginationProps.count === Object.values(selection || {}).filter(e => e.state === 'checked').length) {
            topCheckmarkState = 'checked';
        }
        else if (Object.values(selection || {}).some(e => e.state === 'checked')) {
            topCheckmarkState = 'indeterminate';
        }
        else {
            topCheckmarkState = 'unchecked';
        }
    }
    const SortIcon = sortBy && (sortDirection === 'asc' ? SortAmountUpIcon : SortAmountDownIcon);
    return (React.createElement("div", null,
        React.createElement(CardTableHeader, { header: header, filter: filter, expandableFilters: expandableFilters, filtersExpanded: filtersExpanded, numActiveFilters: numActiveFilters, showModal: showModal, paginationProps: checkedProps, selectableState: topCheckmarkState, onSelectionChange: topSelectionChangeHandler, isMobile: isMobile }),
        !isMobile && (React.createElement("div", { className: classes.expansionTableHeader },
            visibleColumns.map(({ name, isSortable, minWidth, label, isLabelHidden, fixedSize }) => {
                const handleSortClicked = () => isSortable && handleClickSort(name);
                const ResizeSensor = require('resize-sensor');
                const changeSize = () => {
                    const elem = elements[name];
                    if (elem) {
                        const width = elem.clientWidth;
                        onSizeChange(name, width);
                    }
                };
                return (React.createElement("span", { key: name, ref: el => {
                        if (el) {
                            new ResizeSensor(el, changeSize);
                            if (!elements[name] || elements[name] !== el)
                                setElements(elems => ({ ...elems, ...fromPairs([[name, el]]) }));
                        }
                    }, className: isSortable ? classes.columnHover : undefined, style: { flexBasis: minWidth, flexGrow: fixedSize ? 0 : 1, flexShrink: 0 }, onClick: handleSortClicked },
                    React.createElement(Typography, { variant: "caption", color: "textSecondary", fontWeight: "light" }, isLabelHidden ? null : label || capitalize(lowerCase(name))),
                    name === sortBy && SortIcon && React.createElement(SortIcon, { className: classes.columnSortIcon })));
            }),
            React.createElement("span", { style: { flexGrow: 1, flexShrink: 0, flexBasis: RESERVED_SPACE } }))),
        React.createElement(CardTableContext.Provider, { value: {
                isMobile,
                colWidths,
                expandedRows: Array.from(expandedRows),
                setRowExpanded,
                visibleColumns: isMobile ? colDefs : visibleColumns,
                hiddenColumns,
                isResizing,
                selectionMapping: selection,
                onSelectionChange,
            } }, isMobile ? (children) : (React.createElement("div", { style: { display: 'flex' } },
            React.createElement("div", { style: { width: '100%', flexGrow: 0, flexShrink: 0, overflow: 'hidden' } }, children)))),
        checkedProps && (React.createElement(Box, { width: "100%", display: "flex", pt: theme.spacing(0.5), justifyContent: "flex-end" },
            React.createElement(CardTablePaginationControls, Object.assign({}, checkedProps))))));
};
export const widthSum = (cols) => cols.reduce((acc, col) => col.minWidth + acc, 0);
export const CardTable = withSize()(CardTableCore);
export const getColumns = (colDefs, size) => {
    const colsWithPriority = colDefs.map(col => ({
        ...col,
        priority: col.priority !== null && col.priority !== undefined ? col.priority : 1,
    }));
    const minRequiredWidth = widthSum(colsWithPriority.filter(col => col.priority === 1));
    if (minRequiredWidth + RESERVED_SPACE > size.width) {
        return { visibleColumns: [], hiddenColumns: [], colDefs: colsWithPriority, isMobile: true };
    }
    const allPriorities = colsWithPriority.map(({ priority }) => priority);
    const sortedPriorities = Array.from(new Set(allPriorities)).sort((a, b) => a - b);
    const visibleColumnsUnordered = sortedPriorities.reduce((acc, ind) => {
        const totalCurrentWidth = widthSum(acc);
        const additionalColumns = colsWithPriority.filter(col => col.priority === ind);
        const newColumnWidth = widthSum(additionalColumns);
        if (size.width >= totalCurrentWidth + newColumnWidth + RESERVED_SPACE) {
            return [...acc, ...additionalColumns];
        }
        return acc;
    }, []);
    const visibleColumnNames = visibleColumnsUnordered.map(col => col.name);
    const visibleColumns = colsWithPriority.filter(col => visibleColumnNames.includes(col.name));
    const hiddenColumns = colsWithPriority.filter(col => !visibleColumnNames.includes(col.name));
    return { visibleColumns, hiddenColumns, isMobile: false };
};
//# sourceMappingURL=CardTable.js.map